export class InstanceRequestError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}
export async function instanceRequest<T>(
  path: string,
  body?: unknown,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(path, {
    method: body === undefined ? "GET" : "POST",
    credentials: "same-origin",
    cache: "no-store",
    signal,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new InstanceRequestError(
      typeof result.error === "string" &&
        /^[A-Z][A-Z0-9_]{1,100}$/.test(result.error)
        ? result.error
        : "REQUEST_FAILED",
      response.status,
    );
  return result;
}
const messages: Record<string, string> = {
  IMPERSONATION_DENIED:
    "This operator account does not have tenant support access.",
  IMPERSONATION_TENANT_NOT_FOUND: "That tenant was not found.",
  IMPERSONATION_NESTING_FORBIDDEN:
    "Stop the current support context before choosing another tenant.",
  IMPERSONATION_PARENT_TOO_SHORT:
    "Support access needs at least five minutes remaining. Sign in again, or ask the instance owner to increase the operator session duration.",
  OPERATOR_AUTH_RATE_LIMITED: "Too many attempts. Wait before trying again.",
  CONFIGURATION_OWNER_REQUIRED:
    "Only the instance owner can edit configuration.",
  CONFIGURATION_EDIT_CONFLICT:
    "A newer configuration was accepted. Reload saved settings before editing again.",
  CONFIGURATION_APPROVAL_REQUIRED:
    "Review the restart requirement and approve this candidate before applying it.",
  CONFIGURATION_RATE_LIMITED:
    "Too many configuration checks. Wait a minute before retrying.",
  CONFIGURATION_STORAGE_MIGRATION_REQUIRED:
    "Moving stored bytes requires a separate migration. Keep the current storage locations.",
  CONFIGURATION_SECURITY_ROTATION_REQUIRED:
    "Login and request-rate key rotation requires the coordinated offline procedure.",
  CONFIGURATION_RESTART_REQUIRED:
    "Configuration was accepted. Restart every API before reopening traffic.",
  SETUP_DENIED: "The bootstrap token was not accepted.",
  SETUP_CLOSED: "Setup is already complete.",
  SETUP_NOT_AVAILABLE:
    "The API administrator must configure a bootstrap token before browser setup is available.",
  SETUP_RATE_LIMITED:
    "Too many setup requests. Wait a minute before trying again.",
  CONFIGURATION_SETUP_CONFLICT:
    "A different setup was already accepted. Retry the original settings and credentials.",
  CONFIGURATION_POSTGRES_UNAVAILABLE:
    "PostgreSQL could not be reached with these settings.",
  CONFIGURATION_OBJECTS_UNAVAILABLE:
    "The selected object store could not complete its storage checks.",
  CONFIGURATION_IDENTITY_UNAVAILABLE:
    "The host identity adapter did not pass its protocol check.",
  CONFIGURATION_DELIVERY_UNAVAILABLE:
    "The ordering host did not pass its authenticated status check. Verify the endpoint, host ID and service credential. No order was submitted.",
  CONFIGURATION_QUEUE_UNAVAILABLE:
    "Redis could not verify the queue connection, credential or trusted certificate. Check the queue settings and retry.",
  OPERATOR_AUTH_INVALID:
    "The username or password was not accepted, or the session has expired.",
  OPERATOR_PASSWORD_POLICY: "Use a different password of 12–128 characters.",
  PASSWORD_CHANGE_REQUIRED: "Change the temporary password before continuing.",
  ORIGIN_MISMATCH: "Use the configured public address for this action.",
  ORIGIN_REQUIRED: "This action requires the configured public origin.",
  ADAPTER_NOT_AVAILABLE: "A selected provider is not implemented yet.",
  REQUEST_TOO_LARGE: "The request exceeds the supported size.",
  CONFIGURATION_INVALID:
    "Some settings are invalid. Review the selected providers and values.",
};
export function instanceMessage(error: unknown) {
  return error instanceof InstanceRequestError
    ? messages[error.code] ||
        `The request could not be completed (${error.code}).`
    : "The API could not be reached. Check instance status before retrying.";
}
export interface InstanceStatus {
  version: 1;
  mode: "environment" | "instance" | "application";
  phase?:
    "setup-required" | "activating" | "ready" | "degraded" | "restart-required";
  ready?: boolean;
  error?: string;
  setup?: { available: boolean; platform: "windows" | "posix" };
  configuration?: {
    name: string;
    profile: string;
    revision: string;
    publicOrigin: string;
    providers: Record<string, string>;
  };
}
